import AssignmentIcon from "@mui/icons-material/Assignment";
import CakeIcon from "@mui/icons-material/Cake";
import MessageIcon from "@mui/icons-material/Message";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import WarningIcon from "@mui/icons-material/Warning";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Paper, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useCallback, useState } from "react";
import CommunicationTextField from "../../../../components/StaffCommunication/util/CommunicationTextField";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useFormatPersonName } from "../../../../hooks/useNameFormat";
import { INK, STATUS, SURFACE } from "../../../../theme/tokens";
import type { Nurse } from "../../../../utils/models";
import { FirstShiftDialog } from "../WeeklySchedule/FirstShiftDialog";

// ─── Types ───────────────────────────────────────────────────────────────────

type DemoEngagement =
	| { type: "turnoverRisk"; riskLevel: "High" | "Medium"; riskFactors: string[] }
	| { type: "newEmployee"; firstShiftDate: string; assignedTeam: string }
	| { type: "celebration"; eventType: "birthday" | "anniversary"; eventDate: string; nextShift: string; years?: number }
	| { type: "survey"; rating: number; responses: { question: string; answer: string }[] };

// ─── Demo data ────────────────────────────────────────────────────────────────

export function getDemoEngagement(nurse: Nurse): DemoEngagement | null {
	const bucket = nurse.id % 10;
	switch (bucket) {
		case 0:
			return null;
		case 1:
			return {
				type: "turnoverRisk",
				riskLevel: "High",
				riskFactors: ["3 sick days in the last month", "Late twice in the past week", "Short tenure"],
			};
		case 2:
			return {
				type: "newEmployee",
				firstShiftDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toLocaleDateString(),
				assignedTeam: "Night Shift",
			};
		case 3:
			return {
				type: "celebration",
				eventType: nurse.id % 2 === 0 ? "birthday" : "anniversary",
				eventDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString(),
				nextShift: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString(),
				years: 5,
			};
		case 4:
			return {
				type: "survey",
				rating: 2,
				responses: [
					{
						question: "How would you rate your last shift overall?",
						answer: "2. Honestly, a bit stressful. Had a lot of family questions that slowed me down.",
					},
					{
						question: "Did you feel adequately staffed during your shift?",
						answer: "We are drowning. Management sees the RNs are okay and thinks the floor is fine, but the aides are breaking.",
					},
					{
						question: "Did you have the supplies, support, and resources you needed?",
						answer: "Yes, the new supply ordering system seems to be working better this week.",
					},
					{
						question: "Were there any patient care situations you felt understaffed for?",
						answer: "Not at all. The team worked really well together today to make sure everyone was covered.",
					},
				],
			};
		default:
			return null;
	}
}

// ─── Reusable single-message notify dialog ────────────────────────────────────

type NotifyDialogProps = {
	open: boolean;
	onClose: () => void;
	title: string;
	description: string;
	initialMessage: string;
	/** Show "Send 12h Before" in addition to "Send Now" */
	showScheduled?: boolean;
};

const NotifyDialog: FC<NotifyDialogProps> = ({ open, onClose, title, description, initialMessage, showScheduled }) => {
	const [message, setMessage] = useState(initialMessage);
	const handleSend = useCallback(() => onClose(), [onClose]);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="staff-engagement-notify-dialog">
			<DialogTitle color={INK.SECONDARY}>{title}</DialogTitle>
			<DialogContent>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 2,
						pt: 1,
					}}
				>
					<Typography
						variant="body2"
						sx={{
							color: INK.SECONDARY,
						}}
					>
						{description}
					</Typography>
					<CommunicationTextField
						placeholder="Type your message"
						value={message}
						onChange={setMessage}
						onEnter={handleSend}
						maxLines={8}
						data-testid="staff-engagement-notify-message-input"
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Box
					sx={{
						display: "flex",
						gap: 1,
						p: 1,
					}}
				>
					<SystemButton onClick={onClose} buttonVariant="borderless" sx={{ fontSize: "14px" }} data-testid="staff-engagement-notify-cancel-button">
						Cancel
					</SystemButton>
					{showScheduled && (
						<SystemButton onClick={handleSend} buttonVariant="secondary" sx={{ fontSize: "14px" }} data-testid="staff-engagement-notify-schedule-button">
							Send 12h Before
						</SystemButton>
					)}
					<SystemButton onClick={handleSend} buttonVariant="primary" sx={{ fontSize: "14px" }} data-testid="staff-engagement-notify-send-button">
						Send Now
					</SystemButton>
				</Box>
			</DialogActions>
		</Dialog>
	);
};

// ─── Section components ───────────────────────────────────────────────────────

const TurnoverRiskSection: FC<{ engagement: Extract<DemoEngagement, { type: "turnoverRisk" }>; nurse: Nurse }> = ({ engagement, nurse }) => {
	const formatPersonName = useFormatPersonName();
	const [open, setOpen] = useState(false);
	const isHigh = engagement.riskLevel === "High";
	const color = isHigh ? "error" : "warning";

	return (
		<Box>
			<Stack
				direction="row"
				spacing={1}
				sx={{
					alignItems: "center",
					mb: 2,
				}}
			>
				<WarningIcon color={color} />
				<Typography
					variant="h6"
					color={`${color}.main`}
					sx={{
						fontWeight: "bold",
					}}
				>
					Turnover Risk Alert
				</Typography>
				<Chip label={engagement.riskLevel} size="small" color={color} sx={{ ml: 1, fontWeight: "bold" }} />
			</Stack>
			<Paper
				elevation={0}
				sx={{
					p: 3,
					border: "1px solid",
					borderColor: `${color}.light`,
					bgcolor: isHigh ? STATUS.ERROR.light : STATUS.WARNING.light,
					borderRadius: 2,
				}}
			>
				<Typography variant="subtitle2" gutterBottom>
					Why is this person at risk?
				</Typography>
				<Stack
					spacing={1}
					sx={{
						mt: 1,
						mb: 3,
					}}
				>
					{engagement.riskFactors.map((factor) => (
						<Box key={factor} sx={{ display: "flex", alignItems: "flex-start" }}>
							<Box
								sx={{
									width: 6,
									height: 6,
									borderRadius: "50%",
									bgcolor: `${color}.main`,
									mt: 0.75,
									mr: 1.5,
									flexShrink: 0,
								}}
							/>
							<Typography
								variant="body2"
								sx={{
									color: "text.secondary",
								}}
							>
								{factor}
							</Typography>
						</Box>
					))}
				</Stack>
				<Button
					variant="outlined"
					color={color}
					size="small"
					startIcon={<NotificationsActiveIcon />}
					onClick={() => setOpen(true)}
					data-testid="staff-engagement-turnover-notify-button"
				>
					Notify DoN
				</Button>
			</Paper>
			<NotifyDialog
				open={open}
				onClose={() => setOpen(false)}
				title="Notify Director of Nursing"
				description={`This message will be sent to the Director of Nursing regarding ${formatPersonName(nurse)}'s turnover risk.`}
				initialMessage={`Hi, I wanted to flag that ${nurse.firstName} ${nurse.lastName} (${nurse.role}) is showing ${engagement.riskLevel} turnover risk signals.\n\nRisk factors:\n${engagement.riskFactors.map((f) => `• ${f}`).join("\n")}\n\nPlease advise on next steps.`}
			/>
		</Box>
	);
};

const NewEmployeeSection: FC<{ engagement: Extract<DemoEngagement, { type: "newEmployee" }>; nurse: Nurse }> = ({ engagement, nurse }) => {
	const formatPersonName = useFormatPersonName();
	const [welcomeOpen, setWelcomeOpen] = useState(false);
	const [notifyChargeOpen, setNotifyChargeOpen] = useState(false);
	const [welcomeTeamOpen, setWelcomeTeamOpen] = useState(false);

	return (
		<Box>
			<Stack
				direction="row"
				spacing={1}
				sx={{
					alignItems: "center",
					mb: 2,
				}}
			>
				<PersonAddIcon color="success" />
				<Typography
					variant="h6"
					sx={{
						fontWeight: "bold",
						color: "success.main",
					}}
				>
					New Employee Onboarding
				</Typography>
			</Stack>
			<Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "success.light", bgcolor: STATUS.SUCCESS.light, borderRadius: 2 }}>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={4}
					sx={{
						mb: 3,
					}}
				>
					<Box>
						<Typography
							variant="caption"
							sx={{
								color: "text.secondary",
								display: "block",
							}}
						>
							First Shift
						</Typography>
						<Typography
							variant="body1"
							sx={{
								fontWeight: "medium",
							}}
						>
							{engagement.firstShiftDate}
						</Typography>
					</Box>
					<Box>
						<Typography
							variant="caption"
							sx={{
								color: "text.secondary",
								display: "block",
							}}
						>
							Assigned Team
						</Typography>
						<Typography
							variant="body1"
							sx={{
								fontWeight: "medium",
							}}
						>
							{engagement.assignedTeam}
						</Typography>
					</Box>
				</Stack>
				<Divider sx={{ my: 2, borderColor: "success.light" }} />
				<Typography variant="subtitle2" gutterBottom>
					Onboarding Actions:
				</Typography>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={2}
					sx={{
						mt: 1,
					}}
				>
					<Button
						variant="contained"
						color="success"
						size="small"
						startIcon={<MessageIcon />}
						onClick={() => setWelcomeOpen(true)}
						data-testid="staff-engagement-welcome-employee-button"
					>
						Welcome Employee
					</Button>
					<Button
						variant="outlined"
						color="success"
						size="small"
						startIcon={<NotificationsActiveIcon />}
						onClick={() => setNotifyChargeOpen(true)}
						data-testid="staff-engagement-notify-charge-button"
					>
						Notify Charge
					</Button>
					<Button
						variant="outlined"
						color="success"
						size="small"
						startIcon={<MessageIcon />}
						onClick={() => setWelcomeTeamOpen(true)}
						data-testid="staff-engagement-welcome-team-button"
					>
						Welcome to Team
					</Button>
				</Stack>
			</Paper>
			{/* Welcome Employee — reuses FirstShiftDialog (3-step: new staff → team → shift manager) */}
			{welcomeOpen && <FirstShiftDialog open={welcomeOpen} onClose={() => setWelcomeOpen(false)} nurse={nurse} />}
			<NotifyDialog
				open={notifyChargeOpen}
				onClose={() => setNotifyChargeOpen(false)}
				title="Notify Charge Nurse"
				description={`This message will be sent to the Charge Nurse about ${formatPersonName(nurse)}'s upcoming first shift.`}
				initialMessage={`Hi! Just a heads-up that ${nurse.firstName} ${nurse.lastName}, our new ${nurse.role}, is starting their first shift on ${engagement.firstShiftDate} on the ${engagement.assignedTeam}.\n\nPlease make sure to welcome them and help them get settled in. Thank you!`}
				showScheduled
			/>
			<NotifyDialog
				open={welcomeTeamOpen}
				onClose={() => setWelcomeTeamOpen(false)}
				title="Welcome to Team"
				description={`This message will be sent to all team members on the ${engagement.assignedTeam}.`}
				initialMessage={`Hi everyone! 👋\nJust a reminder that ${nurse.firstName} ${nurse.lastName}, our new ${nurse.role}, is joining the ${engagement.assignedTeam} on ${engagement.firstShiftDate}.\n\nFirst shifts can be tough — please say hello and be extra welcoming. Thanks for helping make their first day a great one! 😊`}
				showScheduled
			/>
		</Box>
	);
};

const CelebrationSection: FC<{ engagement: Extract<DemoEngagement, { type: "celebration" }>; nurse: Nurse }> = ({ engagement, nurse }) => {
	const formatPersonName = useFormatPersonName();
	const [open, setOpen] = useState(false);
	const label = engagement.eventType === "birthday" ? "Birthday" : `${engagement.years}-Year Anniversary`;

	return (
		<Box>
			<Stack
				direction="row"
				spacing={1}
				sx={{
					alignItems: "center",
					mb: 2,
				}}
			>
				<CakeIcon color="secondary" />
				<Typography
					variant="h6"
					sx={{
						fontWeight: "bold",
						color: "secondary.main",
						textTransform: "capitalize",
					}}
				>
					Upcoming {engagement.eventType}
				</Typography>
			</Stack>
			<Paper
				elevation={0}
				sx={{ p: 3, border: "1px solid", borderColor: "secondary.light", bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08), borderRadius: 2 }}
			>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={4}
					sx={{
						mb: 3,
					}}
				>
					<Box>
						<Typography
							variant="caption"
							sx={{
								color: "text.secondary",
								display: "block",
							}}
						>
							Event Date
						</Typography>
						<Typography
							variant="body1"
							sx={{
								fontWeight: "medium",
							}}
						>
							{engagement.eventDate}
						</Typography>
					</Box>
					<Box>
						<Typography
							variant="caption"
							sx={{
								color: "text.secondary",
								display: "block",
							}}
						>
							Next Shift
						</Typography>
						<Typography
							variant="body1"
							sx={{
								fontWeight: "medium",
							}}
						>
							{engagement.nextShift}
						</Typography>
					</Box>
					{engagement.years && (
						<Box>
							<Typography
								variant="caption"
								sx={{
									color: "text.secondary",
									display: "block",
								}}
							>
								Years of Service
							</Typography>
							<Typography
								variant="body1"
								sx={{
									fontWeight: "medium",
								}}
							>
								{engagement.years}
							</Typography>
						</Box>
					)}
				</Stack>
				<Button
					variant="contained"
					color="secondary"
					size="small"
					startIcon={<NotificationsActiveIcon />}
					onClick={() => setOpen(true)}
					data-testid="staff-engagement-celebration-notify-button"
				>
					Notify Charge Nurse
				</Button>
			</Paper>
			<NotifyDialog
				open={open}
				onClose={() => setOpen(false)}
				title="Notify Charge Nurse"
				description={`This message will be sent to the Charge Nurse about ${formatPersonName(nurse)}'s upcoming ${engagement.eventType}.`}
				initialMessage={`Hi! Just wanted to let you know that ${nurse.firstName} ${nurse.lastName} has an upcoming ${label} on ${engagement.eventDate}.\n\nTheir next shift is on ${engagement.nextShift} — it would be wonderful to acknowledge this then. Thank you! 🎉`}
				showScheduled
			/>
		</Box>
	);
};

const SurveySection: FC<{ engagement: Extract<DemoEngagement, { type: "survey" }>; nurse: Nurse }> = ({ engagement, nurse }) => {
	const formatPersonName = useFormatPersonName();
	const [open, setOpen] = useState(false);

	return (
		<Box>
			<Stack
				direction="row"
				spacing={1}
				sx={{
					alignItems: "center",
					mb: 2,
				}}
			>
				<AssignmentIcon color="info" />
				<Typography
					variant="h6"
					sx={{
						fontWeight: "bold",
						color: "info.main",
					}}
				>
					Recent Survey Responses
				</Typography>
				<Chip label={`Rating: ${engagement.rating}/5`} size="small" color={engagement.rating <= 2 ? "error" : "success"} variant="outlined" sx={{ ml: 1 }} />
			</Stack>
			<Paper elevation={0} sx={{ p: 0, border: "1px solid", borderColor: "info.light", borderRadius: 2, overflow: "hidden" }}>
				{engagement.responses.map((resp, idx) => (
					<Box
						key={resp.question}
						sx={{
							p: 2,
							borderBottom: idx < engagement.responses.length - 1 ? "1px solid" : "none",
							borderColor: "divider",
							bgcolor: STATUS.INFO.light,
						}}
					>
						<Typography
							variant="caption"
							sx={{
								color: "text.secondary",
								display: "block",
								mb: 0.5,
							}}
						>
							Question:
						</Typography>
						<Typography
							variant="body2"
							sx={{
								fontWeight: "medium",
								mb: 1,
							}}
						>
							{resp.question}
						</Typography>
						<Typography
							variant="caption"
							sx={{
								color: "text.secondary",
								display: "block",
								mb: 0.5,
							}}
						>
							Answer:
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.primary",
								fontStyle: "italic",
							}}
						>
							"{resp.answer}"
						</Typography>
					</Box>
				))}
				<Box sx={{ p: 2, bgcolor: SURFACE, borderTop: "1px solid", borderColor: "divider" }}>
					<Button
						variant="contained"
						color="info"
						size="small"
						startIcon={<MessageIcon />}
						onClick={() => setOpen(true)}
						data-testid="staff-engagement-survey-followup-button"
					>
						Ask Follow-up Questions
					</Button>
				</Box>
			</Paper>
			<NotifyDialog
				open={open}
				onClose={() => setOpen(false)}
				title="Ask Follow-up Questions"
				description={`This message will be sent directly to ${formatPersonName(nurse)} as a follow-up to their survey responses.`}
				initialMessage={`Hi ${nurse.firstName}, thank you for taking the time to complete your survey.\n\nI wanted to follow up on some of your responses. Could we find a time to chat this week? I'd love to hear more about your experience and see how we can better support you.`}
			/>
		</Box>
	);
};

// ─── Main export ──────────────────────────────────────────────────────────────

type Props = { nurse: Nurse };

export const StaffEngagementSection: FC<Props> = ({ nurse }) => {
	const engagement = getDemoEngagement(nurse);
	if (!engagement) return null;

	return (
		<Box data-testid="profile-staff-engagement-section" sx={{ width: "100%" }}>
			<Typography
				variant="subtitle1"
				sx={{
					fontWeight: "bold",
					color: "text.secondary",
					mb: 2,
				}}
			>
				Staff Engagement
			</Typography>
			{engagement.type === "turnoverRisk" && <TurnoverRiskSection engagement={engagement} nurse={nurse} />}
			{engagement.type === "newEmployee" && <NewEmployeeSection engagement={engagement} nurse={nurse} />}
			{engagement.type === "celebration" && <CelebrationSection engagement={engagement} nurse={nurse} />}
			{engagement.type === "survey" && <SurveySection engagement={engagement} nurse={nurse} />}
		</Box>
	);
};
